<script setup lang="ts">
import { useSaleStore } from '../stores/sales'
import { useClientStore } from '../stores/clients'
import { useInventoryStore } from '../stores/inventory'
import { useRouter } from 'vue-router'
import { format, isValid } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { ref, computed, onMounted, watch } from 'vue'
import type { Sale } from '../types'
import { formatCurrency } from '../utils/format'
import { UiModal, UiButton, UiCard, UiAlert, UiLoader, UiBadge } from '../components/ui'

const saleStore = useSaleStore()
const clientStore = useClientStore()
const inventoryStore = useInventoryStore()
const router = useRouter()

const filters = ref({ dateStart: '', dateEnd: '', client: '', item: '' })
const sortKey = ref<keyof Sale | 'clientName'>('date')
const sortOrder = ref('desc')
const currentPage = ref(1)
const itemsPerPage = 10

const sales = computed(() => saleStore.sales)
const clients = computed(() => clientStore.clients)
const products = computed(() => inventoryStore.products)
const isLoading = computed(() => saleStore.isLoading)
const error = computed(() => saleStore.error)

const clientSuggestions = ref<any[]>([])
const productSuggestions = ref<any[]>([])
const deleteConfirmId = ref<string | null>(null)

onMounted(() => {
  saleStore.fetchSales()
  clientStore.fetchClients()
  inventoryStore.fetchProducts()
})

function getClientName(clientId: string) {
  return clientStore.getClient(clientId)?.name || 'Cliente desconhecido'
}

function getProductName(productId: string) {
  return inventoryStore.getProduct(productId)?.name || 'Produto desconhecido'
}

function formatDate(date: Date | string | number) {
  const d = new Date(date)
  return isValid(d) ? format(d, 'dd/MM/yyyy', { locale: ptBR }) : 'Data inválida'
}

const filteredSales = computed(() => {
  let filtered = sales.value
  if (filters.value.dateStart) {
    const start = new Date(filters.value.dateStart)
    start.setHours(0, 0, 0, 0)
    filtered = filtered.filter(sale => new Date(sale.date) >= start)
  }
  if (filters.value.dateEnd) {
    const end = new Date(filters.value.dateEnd)
    end.setHours(23, 59, 59, 999)
    filtered = filtered.filter(sale => new Date(sale.date) <= end)
  }
  if (filters.value.client) {
    filtered = filtered.filter(sale =>
      getClientName(sale.clientId).toLowerCase().includes(filters.value.client.toLowerCase())
    )
  }
  if (filters.value.item) {
    filtered = filtered.filter(sale =>
      sale.items.some(item =>
        getProductName(item.productId).toLowerCase().includes(filters.value.item.toLowerCase())
      )
    )
  }
  return filtered
})

const sortedSales = computed(() => {
  return [...filteredSales.value].sort((a, b) => {
    let aValue: any, bValue: any
    if (sortKey.value === 'clientName') {
      aValue = getClientName(a.clientId)
      bValue = getClientName(b.clientId)
    } else {
      aValue = a[sortKey.value as keyof Sale]
      bValue = b[sortKey.value as keyof Sale]
    }
    if (aValue < bValue) return sortOrder.value === 'asc' ? -1 : 1
    if (aValue > bValue) return sortOrder.value === 'asc' ? 1 : -1
    return 0
  })
})

const hasActiveFilters = computed(() =>
  Object.values(filters.value).some(v => v !== '')
)

const filteredTotal = computed(() =>
  filteredSales.value.reduce((sum, sale) => sum + sale.total, 0)
)

const paginatedSales = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage
  return sortedSales.value.slice(start, start + itemsPerPage)
})

const totalPages = computed(() => Math.ceil(sortedSales.value.length / itemsPerPage))

function changePage(page: number) {
  if (page >= 1 && page <= totalPages.value) currentPage.value = page
}

function sortBy(key: keyof Sale | 'clientName') {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

function sortIndicator(key: string) {
  if (sortKey.value !== key) return ''
  return sortOrder.value === 'asc' ? ' ↑' : ' ↓'
}

watch(() => filters.value.client, (val) => {
  clientSuggestions.value = val
    ? clients.value.filter(c => c.name.toLowerCase().includes(val.toLowerCase()))
    : []
})

watch(() => filters.value.item, (val) => {
  productSuggestions.value = val
    ? products.value.filter(p => p.name.toLowerCase().includes(val.toLowerCase()))
    : []
})

function selectClient(name: string) {
  filters.value.client = name
  clientSuggestions.value = []
}

function selectProduct(name: string) {
  filters.value.item = name
  productSuggestions.value = []
}

function editSale(saleId: string) {
  router.push(`/sales/edit/${saleId}`)
}

function confirmDelete(id: string) {
  deleteConfirmId.value = id
}

async function handleDelete() {
  if (!deleteConfirmId.value) return
  await saleStore.deleteSale(deleteConfirmId.value)
  deleteConfirmId.value = null
}
</script>

<template>
  <div class="page-container">
    <div class="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 class="text-xl font-bold text-surface-800 dark:text-surface-100 sm:text-3xl">Vendas</h1>
        <p class="mt-1 text-xs text-surface-500 dark:text-surface-400 sm:text-sm">Histórico e gerenciamento de vendas</p>
      </div>
      <UiButton @click="router.push('/sales/new')">
        <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        <span class="hidden sm:inline">Nova Venda</span>
        <span class="sm:hidden">Nova</span>
      </UiButton>
    </div>

    <UiAlert variant="error" :message="error || ''" dismissible @dismiss="saleStore.error = null" />

    <div class="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <div>
        <label class="label">Data Inicial</label>
        <input type="date" v-model="filters.dateStart" class="input" />
      </div>
      <div>
        <label class="label">Data Final</label>
        <input type="date" v-model="filters.dateEnd" class="input" />
      </div>
      <div class="relative">
        <label class="label">Cliente</label>
        <input type="text" v-model="filters.client" placeholder="Filtrar por cliente..." class="input" />
        <ul
          v-if="clientSuggestions.length"
          class="absolute z-10 mt-1 w-full rounded-xl border bg-white dark:border-surface-700 dark:bg-surface-900 py-1 shadow-soft-lg"
        >
          <li
            v-for="s in clientSuggestions"
            :key="s.id"
            @click="selectClient(s.name)"
            class="cursor-pointer px-4 py-2 text-sm text-surface-700 dark:text-surface-200 transition-colors hover:bg-surface-50 dark:hover:bg-surface-800"
          >
            {{ s.name }}
          </li>
        </ul>
      </div>
      <div class="relative">
        <label class="label">Item</label>
        <input type="text" v-model="filters.item" placeholder="Filtrar por item..." class="input" />
        <ul
          v-if="productSuggestions.length"
          class="absolute z-10 mt-1 w-full rounded-xl border bg-white dark:border-surface-700 dark:bg-surface-900 py-1 shadow-soft-lg"
        >
          <li
            v-for="s in productSuggestions"
            :key="s.id"
            @click="selectProduct(s.name)"
            class="cursor-pointer px-4 py-2 text-sm text-surface-700 dark:text-surface-200 transition-colors hover:bg-surface-50 dark:hover:bg-surface-800"
          >
            {{ s.name }}
          </li>
        </ul>
      </div>
    </div>

    <UiCard padding>
      <UiLoader v-if="isLoading && !sales.length" text="Carregando vendas..." />

      <div v-else-if="paginatedSales.length === 0 && !error" class="flex flex-col items-center py-16 text-center">
        <div class="mb-4 rounded-full bg-surface-100 dark:bg-surface-800 p-4">
          <svg class="h-8 w-8 text-surface-400 dark:text-surface-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
          </svg>
        </div>
        <h3 class="text-lg font-semibold text-surface-700 dark:text-surface-200">Nenhuma venda encontrada</h3>
        <p class="mt-1 text-sm text-surface-400 dark:text-surface-500">Tente ajustar os filtros ou crie uma nova venda</p>
      </div>

      <div v-else class="table-wrap">
        <table>
          <thead>
            <tr>
              <th class="sales-col-date cursor-pointer select-none hover:text-surface-700 dark:hover:text-surface-100" @click="sortBy('date')">
                Data<span class="text-surface-300 dark:text-surface-500">{{ sortIndicator('date') }}</span>
              </th>
              <th class="sales-col-clientName cursor-pointer select-none hover:text-surface-700 dark:hover:text-surface-100" @click="sortBy('clientName')">
                Cliente<span class="text-surface-300 dark:text-surface-500">{{ sortIndicator('clientName') }}</span>
              </th>
              <th class="hidden md:table-cell">Itens</th>
              <th class="sales-col-total cursor-pointer select-none hover:text-surface-700 dark:hover:text-surface-100" @click="sortBy('total')">
                Total<span class="text-surface-300 dark:text-surface-500">{{ sortIndicator('total') }}</span>
              </th>
              <th class="sales-col-actions">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="sale in paginatedSales" :key="sale.id">
              <td class="text-surface-600 dark:text-surface-300">{{ formatDate(sale.date) }}</td>
              <td>
                <div class="font-medium text-surface-800 dark:text-surface-100">{{ getClientName(sale.clientId) }}</div>
                <!-- Show items inline on mobile -->
                <div class="mt-1 flex flex-wrap gap-1 md:hidden">
                  <UiBadge
                    v-for="item in sale.items"
                    :key="item.productId"
                    variant="slate"
                    :label="`${getProductName(item.productId)} (${item.quantity}x)`"
                  />
                </div>
              </td>
              <td class="hidden md:table-cell">
                <div class="flex flex-wrap gap-1">
                  <UiBadge
                    v-for="item in sale.items"
                    :key="item.productId"
                    variant="slate"
                    :label="`${getProductName(item.productId)} (${item.quantity}x)`"
                  />
                </div>
              </td>
              <td class="font-mono font-semibold text-surface-800 dark:text-surface-100">{{ formatCurrency(sale.total) }}</td>
              <td>
                <div class="flex items-center gap-1 sm:gap-2">
                  <UiButton variant="ghost" size="sm" @click="editSale(sale.id)">
                    <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span class="hidden sm:inline">Editar</span>
                  </UiButton>
                  <UiButton variant="ghost" size="sm" @click="confirmDelete(sale.id)">
                    <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span class="hidden sm:inline">Excluir</span>
                  </UiButton>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="hasActiveFilters">
            <tr>
              <td colspan="2" class="text-right text-sm font-semibold text-surface-600 dark:text-surface-300 sm:hidden">Total filtrado:</td>
              <td colspan="3" class="text-right text-sm font-semibold text-surface-600 dark:text-surface-300 hidden sm:table-cell">Total filtrado:</td>
              <td class="font-mono font-bold text-purple-700 dark:text-purple-400">{{ formatCurrency(filteredTotal) }}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div
        v-if="totalPages > 1"
        class="flex items-center justify-between border-t px-6 py-4"
      >
        <p class="text-sm text-surface-500 dark:text-surface-400">
          Página {{ currentPage }} de {{ totalPages }}
        </p>
        <div class="flex items-center gap-2">
          <UiButton variant="secondary" size="sm" :disabled="currentPage <= 1" @click="changePage(currentPage - 1)">
            Anterior
          </UiButton>
          <UiButton variant="secondary" size="sm" :disabled="currentPage >= totalPages" @click="changePage(currentPage + 1)">
            Próxima
          </UiButton>
        </div>
      </div>
    </UiCard>

    <UiModal
      :open="!!deleteConfirmId"
      title="Excluir Venda"
      max-width="max-w-sm"
      @close="deleteConfirmId = null"
    >
      <p class="text-sm text-surface-600 dark:text-surface-300">
        Tem certeza que deseja excluir esta venda? Esta ação não pode ser desfeita.
      </p>
      <template #footer>
        <UiButton variant="secondary" @click="deleteConfirmId = null">Cancelar</UiButton>
        <UiButton variant="danger" :loading="isLoading" @click="handleDelete">Excluir</UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
/* Mobile: shrink actions, expand data & total columns */
@media (max-width: 639px) {
  .sales-col-date {
    width: 21%;
    /* min-width: 5.5rem; */
  }

   .sales-col-clientName {
    width: 21%;
    min-width: 3.5rem;
  }

  .sales-col-total {
    width: 29%;
    min-width: 5rem;
  }
  .sales-col-actions {
    width: 1%;
    /* max-width: 1.2rem; */
    /* padding-left: 0.25rem !important; */
    /* padding-right: 0.25rem !important; */
  }
}

/* Desktop: normal sizing */
@media (min-width: 640px) {
  .sales-col-actions {
    width: 10rem;
  }
}
</style>
